import React, { useState, useEffect, useMemo } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import axios from 'axios';
import { Table, Badge, Button, Pagination, Row, Col, Form } from 'react-bootstrap';
import AddTripRequest from './PopUps/AddTripRequest';
import EditTripRequest from './PopUps/EditTripRequest';
import * as Icon from 'react-feather';
import { canPageAction } from '../../config/roleConfig';
import DeactivateConfirmModal from '../Common/DeactivateConfirmModal';

function TripRequests() {
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const user = authUser();
    const canWrite = canPageAction(user, 'client.trip_requests', 'write');
    const canDelete = canPageAction(user, 'client.trip_requests', 'delete');

    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [requestToDeactivate, setRequestToDeactivate] = useState(null);
    const [deactivatingRequest, setDeactivatingRequest] = useState(false);
    const apiUrl = process.env.REACT_APP_URL;

    // Filter states
    const [statusFilter, setStatusFilter] = useState('');
    const [showFilters, setShowFilters] = useState(true);

    // Check if user is a Cliente role
    const isClienteUser = user?.roles?.some(role => role.title === 'Cliente');
    const clientId = user?.client_id;

    const getTripRequests = () => {
        let url = `${apiUrl}/trips/get_all_trip_requests`;

        // If user is a Cliente, filter by their client_id
        if (isClienteUser && clientId) {
            url += `?client_id=${clientId}`;
        }

        axios({
            method: 'GET',
            url: url,
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.TripRequests || []);
        }).catch(error => {
            alert(error);
            console.log(error);
        });
    }

    useEffect(() => {
        getTripRequests();
    }, []);

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return <Badge bg="success">Aprobado</Badge>;
            case 'pending':
                return <Badge bg="warning">Pendiente</Badge>;
            case 'rejected':
                return <Badge bg="danger">Rechazado</Badge>;
            default:
                return <Badge bg="secondary">{status || 'N/A'}</Badge>;
        }
    };

    const refreshData = () => {
        getTripRequests();
    };

    const deleteTripRequest = (request) => {
        if (!request) {
            return;
        }

        setDeactivatingRequest(true);
        axios({
            method: 'DELETE',
            url: `${apiUrl}/trips/delete_trip_request/${request.id}`,
            headers: {
                Authorization: authHeader()
            }
        }).then(() => {
            setRequestToDeactivate(null);
            refreshData();
        }).catch(error => {
            alert(error.response?.data?.error || 'No se pudo desactivar la solicitud');
            console.log(error);
        }).finally(() => {
            setDeactivatingRequest(false);
        });
    };

    // Filter logic using useMemo for performance
    const filteredData = useMemo(() => {
        return data.filter(request => {
            // Filter by status
            if (statusFilter && request.status !== statusFilter) {
                return false;
            }

            return true;
        });
    }, [data, statusFilter]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    const clearFilters = () => {
        setStatusFilter('');
    };

    const hasActiveFilters = statusFilter;

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
        <>
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between">
                    <h5 className="card-title">Solicitudes de Viaje</h5>
                    <div className="d-flex gap-2">
                        <Button
                            variant={showFilters ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Icon.Filter size={14} className="me-1" />
                            Filtros {hasActiveFilters && <Badge bg="danger" className="ms-1">1</Badge>}
                        </Button>
                        {/* Only show Add button for Cliente users */}
                        {isClienteUser && canWrite && <AddTripRequest setData={refreshData} clientId={clientId} />}
                    </div>
                </div>

                {/* Filter Section */}
                {showFilters && (
                    <div className="bg-light p-3 rounded mb-3">
                        <Row className="g-2 align-items-end">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Estado</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        <option value="pending">Pendiente</option>
                                        <option value="approved">Aprobado</option>
                                        <option value="rejected">Rechazado</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Mostrando {filteredData.length} de {data.length} solicitudes
                                </small>
                                {hasActiveFilters && (
                                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                                        <Icon.X size={14} className="me-1" />
                                        Limpiar Filtros
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                )}

                <div>
                    <Table className="m-0" responsive>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Cliente</th>
                                <th>Ruta</th>
                                <th>Carga</th>
                                <th>Fecha Solicitada Inicio</th>
                                <th>Fecha Solicitada Fin</th>
                                <th>Estado</th>
                                <th>Solicitado Por</th>
                                <th>Fecha Creación</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(request => (
                                <tr key={request.id}>
                                    <td>{request.id}</td>
                                    <td>{request.client_name}</td>
                                    <td>{request.route_name || 'N/A'}</td>
                                    <td>{request.details || 'N/A'}</td>
                                    <td>{request.requested_start_date || 'N/A'}</td>
                                    <td>{request.requested_end_date || 'N/A'}</td>
                                    <td>{getStatusBadge(request.status)}</td>
                                    <td>{request.requested_by_name}</td>
                                    <td>{request.created}</td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            {canWrite && (
                                                <EditTripRequest
                                                    setData={refreshData}
                                                    requestInfo={request}
                                                    isClienteUser={isClienteUser}
                                                />
                                            )}
                                            {canDelete && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => setRequestToDeactivate(request)}
                                                >
                                                    <Icon.Trash2 size={14} />
                                                </Button>
                                            )}
                                            {!canWrite && !canDelete && (
                                                <span className="text-muted">N/A</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {currentItems.length === 0 && (
                                <tr>
                                    <td colSpan="10" className="text-center text-muted">
                                        No hay solicitudes de viaje registradas
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                />

                                {Array.from({ length: totalPages }, (_, index) => {
                                    const page = index + 1;
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 2 && page <= currentPage + 2)
                                    ) {
                                        return (
                                            <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </Pagination.Item>
                                        );
                                    } else if (
                                        page === currentPage - 3 ||
                                        page === currentPage + 3
                                    ) {
                                        return <Pagination.Ellipsis key={page} />;
                                    }
                                    return null;
                                })}

                                <Pagination.Next
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    <div className="mt-2 text-center text-muted">
                        Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredData.length)} de {filteredData.length} solicitudes
                        {hasActiveFilters && <span> (filtrado de {data.length} total)</span>}
                    </div>
                </div>
            </div>
        </div>
        <DeactivateConfirmModal
            show={Boolean(requestToDeactivate)}
            title="Desactivar Solicitud"
            itemLabel={requestToDeactivate ? `Solicitud #${requestToDeactivate.id}` : ''}
            confirming={deactivatingRequest}
            onCancel={() => !deactivatingRequest && setRequestToDeactivate(null)}
            onConfirm={() => deleteTripRequest(requestToDeactivate)}
        />
        </>
    );
}

export default TripRequests;
